import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

/**
 * SEO Component - Manages meta tags, Open Graph, and structured data using react-helmet-async.
 */
const SEO = ({
  title = 'PROTEGE QV Lecture+ | Bibliothèque Communautaire à Yaoundé',
  description = "Centre de Lecture et Bibliothèque Communautaire de PROTEGE QV à Yaoundé. Trouvez des livres, rejoignez des clubs de lecture et participez à nos événements locaux au Cameroun.",
  keywords = 'bibliothèque, lecture, livres, Yaoundé, Cameroun, éducation, culture, réservation, groupes de lecture, centre de lecture à Yaoundé, bibliothèque en ligne',
  image = '/assets/logo/logo-2.jpeg',
  type = 'website',
  author = 'PROTEGE QV',
  canonicalUrl,
  structuredData,
  noIndex = false,
}) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://protegeqvlecture.org';

  // Always canonicalize to siteUrl + current pathname by default (no querystring).
  const currentUrl = canonicalUrl || `${siteUrl}${location.pathname}`;
  const imageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;

  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'LocalBusiness',
        '@id': `${siteUrl}/#localbusiness`,
        name: 'PROTEGE QV Lecture+',
        image: `${siteUrl}/assets/logo/logo-2.jpeg`,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Yaoundé',
          addressRegion: 'Centre',
          addressCountry: 'CM',
        },
        url: siteUrl,
        telephone: '+237xxxxxxxxx', // Default placeholder, client can update in Content
      },
      {
        '@type': 'Library',
        '@id': `${siteUrl}/#library`,
        name: 'Bibliothèque PROTEGE QV',
        url: siteUrl,
        location: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Yaoundé',
            addressRegion: 'Centre',
            addressCountry: 'CM',
          }
        }
      },
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        url: siteUrl,
        name: 'PROTEGE QV Lecture+',
        publisher: { '@id': `${siteUrl}/#localbusiness` },
        inLanguage: 'fr',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${siteUrl}/books?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${currentUrl}#webpage`,
        url: currentUrl,
        name: title,
        description,
        isPartOf: { '@id': `${siteUrl}/#website` },
        about: { '@id': `${siteUrl}/#localbusiness` },
        inLanguage: 'fr',
      },
    ],
  };

  const finalStructured = structuredData || defaultStructuredData;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="PROTEGE QV Lecture+" />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Theme and Format */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#4CAF50" />

      {/* Canonical and Alternatives */}
      <link rel="canonical" href={currentUrl} />
      <link rel="alternate" hrefLang="fr" href={currentUrl} />
      <link rel="alternate" hrefLang="x-default" href={currentUrl} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructured)}
      </script>

      {/* Preconnect Hints */}
      <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
    </Helmet>
  );
};

export default SEO;
